import ImageType from 'models/types/ts/imageType'
import React from 'react'
import ReactMarkdown from 'utils/markdown'
import { UserContext } from 'utils/context/UserContext'
import cx from 'classnames'
import { ButtonWithTarget } from 'layouts/button-with-target'
import { useSelector } from 'react-redux'
import { GlobalState } from 'types'
import { getLoggedInJwt } from 'packages/authentication'
import getCharacters from 'services/api/customer/characters'
import { Character } from 'types/account'
import { Select } from '@mantine/core'
import { ClipLoader } from 'react-spinners'
import { RichTextMicroCopy } from 'pages/news/components/newsDetail/components/rich-text/RichText'
import { PlexForGoodTranslations } from 'pages/plex-for-good/PlexForGood'
import { getPlainTextFromRichText } from 'pages/news/components/newsDetail/components/rich-text/getRenderOptions'
import style from './DonationForm.module.scss'

interface DonationCampaign {
    id: string
    name: string
    title: string
    description: string
    icon?: string
    imageFile: ImageType
}

interface DonationFormProps {
    campaigns: DonationCampaign[]
    translations: PlexForGoodTranslations
}

interface ItemProps extends React.ComponentPropsWithoutRef<'div'> {
    value: string
    label: string
}

const SelectItem = React.forwardRef<HTMLDivElement, ItemProps>(
    ({ value, label, ...others }: ItemProps, ref) => (
        <div ref={ref} {...others}>
            <div className={style.characterSelectValue}>
                <div className={style.characterProfileImageContainer}>
                    <img
                        src={`https://image.eveonline.com/Character/${value}_512.jpg`}
                        alt={label}
                        title={label}
                    />
                </div>
                {label}
            </div>
        </div>
    )
)

const DonationForm = ({
    campaigns,
    translations,
}: DonationFormProps): JSX.Element => {
    const { plexBalance } = React.useContext(UserContext)
    const [
        selectedCampagin,
        setSelectedCampagin,
    ] = React.useState<DonationCampaign>(campaigns[0])
    const [slectedCharacter, setSelectedCharacter] = React.useState<string>()
    const [donationAmount, setDonationAmount] = React.useState<number>(0)
    const userToken = useSelector((state: GlobalState) => getLoggedInJwt(state))
    const [characters, setCharacters] = React.useState<Character[]>([])
    const [loading, setLoading] = React.useState(false)
    React.useEffect(() => {
        getCharacters(userToken).then((data) => {
            setCharacters(
                data.length
                    ? data
                    : [
                          { id: 837211098, name: 'Jin Lay' },
                          { id: 2123215264, name: 'Dr Boberino' },
                          { id: 2123215265, name: 'Hamburgler Dood' },
                      ]
            )
        })
    }, [])

    const numberFormatter = new Intl.NumberFormat()

    const inputPlaceholder = getPlainTextFromRichText(
        translations.enterAmountOf
    )

    return (
        <div className={style.container}>
            <div className={style.imageContainer}>
                <ReactMarkdown
                    className={style.description}
                    source={selectedCampagin.description}
                />
                <img
                    alt={selectedCampagin?.imageFile?.description}
                    src={selectedCampagin?.imageFile?.url}
                />
            </div>
            <div className={style.formContainer}>
                <div className={style.title}>{selectedCampagin.title}</div>
                <div className={style.formDescription}>
                    <RichTextMicroCopy
                        document={translations.donationFormDescription}
                    />
                </div>
                <div className={style.campaginSelector}>
                    {campaigns.length > 1
                        ? campaigns.map((campaign) => {
                              return (
                                  <div
                                      onClick={() =>
                                          setSelectedCampagin(campaign)
                                      }
                                      className={cx(
                                          style.campaginSelectorValue,
                                          {
                                              [style.active]:
                                                  campaign.id ===
                                                  selectedCampagin.id,
                                          }
                                      )}
                                  >
                                      <div className={style.campaignIcon}>
                                          <img
                                              alt={campaign.name}
                                              src={campaign.icon}
                                          />
                                      </div>
                                      <div>{campaign.name}</div>
                                  </div>
                              )
                          })
                        : null}
                </div>
                <form className={style.formBody}>
                    <div className={style.plexInputContainer}>
                        <span className={style.inputWrap}>
                            <span className={style.widthGenerator}>
                                {donationAmount || inputPlaceholder}
                            </span>
                            <input
                                placeholder={inputPlaceholder}
                                min="0"
                                value={
                                    donationAmount
                                        ? Number(donationAmount).toString()
                                        : undefined
                                }
                                onChange={(event) => {
                                    if (
                                        ((event.nativeEvent as unknown) as {
                                            data: string
                                        }).data === '-'
                                    ) {
                                        setDonationAmount((prev) => prev)
                                    } else {
                                        const value = Number(
                                            (
                                                event.target.value || ''
                                            ).replaceAll('-', '') || 0
                                        )
                                        if (
                                            value <= plexBalance &&
                                            value >= 0
                                        ) {
                                            setDonationAmount((prev) => value)
                                        }
                                    }
                                }}
                                type="number"
                                id="plexAmount"
                                name="plexAmount"
                                pattern="^[1-9][0-9]*"
                            />
                        </span>
                        <span>PLEX</span>
                    </div>
                    <div className={style.plexBalance}>
                        <div>
                            <RichTextMicroCopy
                                document={translations.plexBalance}
                            />
                            {numberFormatter.format(plexBalance)}
                        </div>
                        <a href="https://store.eveonline.com#plex">
                            <RichTextMicroCopy
                                document={translations.needMorePlex}
                            />
                        </a>
                    </div>
                    <div className={style.plexButtons}>
                        {[10, 100, 1000, plexBalance].map((amount) => {
                            return (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setDonationAmount(amount)
                                    }}
                                    disabled={amount > plexBalance}
                                >
                                    {numberFormatter.format(amount)} PLEX
                                </button>
                            )
                        })}
                    </div>
                    <Select
                        clearable
                        maxDropdownHeight={300}
                        classNames={{
                            input: style.characterSelect,
                            itemsWrapper: style.selectItemsWrapper,
                            item: style.selectItem,
                            dropdown: style.dropdown,
                        }}
                        placeholder={getPlainTextFromRichText(
                            translations.selectCharacterPlaceHolder
                        )}
                        itemComponent={SelectItem}
                        onChange={(option) => {
                            setSelectedCharacter(option)
                        }}
                        value={slectedCharacter}
                        data={characters.map(({ id, name }) => ({
                            value: String(id),
                            label: name,
                        }))}
                    />
                    <div
                        onClick={() => {
                            if (!loading && donationAmount) {
                                setLoading(true)
                                console.log('Donate!!')
                                new Promise((resolve) => {
                                    setTimeout(() => {
                                        resolve(true)
                                    }, 2000)
                                }).finally(() => {
                                    setLoading(false)
                                })
                            }
                        }}
                    >
                        <ButtonWithTarget
                            disabled={!donationAmount || loading}
                            targetClassNames={{ root: style.targetRoot }}
                            buttonClassNames={{ root: style.buttonRoot }}
                            backgroundColor={
                                !donationAmount || loading
                                    ? '#f67c0f33'
                                    : '#F67C0F'
                            }
                        >
                            <RichTextMicroCopy
                                document={translations.donatePlex}
                            />
                        </ButtonWithTarget>
                    </div>
                </form>
                {loading && (
                    <div className={style.loadingContainer}>
                        <ClipLoader size={80} color="#F67C0F" loading />
                    </div>
                )}
            </div>
        </div>
    )
}

export default DonationForm
